import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  FileText, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Trash2,
  Save,
  FileUp,
  Fuel
} from 'lucide-react';
import { useExpenses } from '../contexts/ExpenseContext';
import * as pdfjsLib from 'pdfjs-dist';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

interface ParsedFuelExpense {
  id: string;
  date: string;
  location: string;
  fuelAmount: number;
  fuelDiscount: number;
  totalAmount: number;
  selected: boolean;
}

interface ParsedToll {
  id: string;
  date: string;
  description: string;
  amount: number;
  selected: boolean;
}

export default function ImportComdata() {
  const { addExpense } = useExpenses();
  const [pdfText, setPdfText] = useState('');
  const [parsedFuel, setParsedFuel] = useState<ParsedFuelExpense[]>([]);
  const [parsedTolls, setParsedTolls] = useState<ParsedToll[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importStatus, setImportStatus] = useState<{ fuel: number; tolls: number; errors: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Parse individual fuel receipts (Love's, Pilot, Flying J, Murphy, etc.)
  const parseIndividualReceipts = (text: string) => {
    const fuelExpenses: ParsedFuelExpense[] = [];
    const tolls: ParsedToll[] = [];
    
    // Split by receipt boundaries (store headers or transaction separators)
    const sections = text.split(/(?=Love's|Pilot|Flying J|Murphy USA|7-ELEVEN|SPEEDWAY|MARATHON|JJ'S)/i);
    
    for (const section of sections) {
      if (!section.trim()) continue;
      
      // Extract date - various formats
      const dateMatch = section.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
      if (!dateMatch) continue;
      
      const [, month, day, year] = dateMatch;
      const fullYear = year.length === 2 ? `20${year}` : year;
      const formattedDate = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      
      // Extract location/store
      const locationMatch = section.match(/(Love's|Pilot|Flying J|Murphy USA|7-ELEVEN|SPEEDWAY|MARATHON|JJ'S)[^\n]*/i) ||
                           section.match(/STORE\s*[#:]?\s*(\d+)/i) ||
                           section.match(/#(\d{3,4})/);
      let location = 'Unknown Location';
      if (locationMatch) {
        const storeLine = section.match(/(Love's|Pilot|Flying J|Murphy USA|7-ELEVEN|SPEEDWAY|MARATHON|JJ'S)[\s#]*(\d+)?/i);
        const cityMatch = section.match(/\n([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),?\s*([A-Z]{2})/);
        if (storeLine && cityMatch) {
          location = `${storeLine[1]}${storeLine[2] ? ' #' + storeLine[2] : ''} - ${cityMatch[1]}, ${cityMatch[2]}`;
        } else if (storeLine) {
          location = `${storeLine[1]}${storeLine[2] ? ' #' + storeLine[2] : ''}`;
        }
      }
      
      // Check if it's a toll receipt
      if (section.match(/TOLL|Toll Road/i)) {
        const tollAmountMatch = section.match(/Total[:\s]*\$?\s*([\d.]+)/i);
        if (tollAmountMatch) {
          const tollState = section.match(/(INDIANA|OHIO|PENNSYLVANIA|ILLINOIS|KENTUCKY)\s+TOLL/i);
          const from = section.match(/From[:\s]*([^\n]+)/i);
          const to = section.match(/To[:\s]*([^\n]+)/i);
          
          const tollRoad = tollState ? `${tollState[1]} Toll Road` : 'Toll Road';
          const route = (from && to) ? ` - ${from[1]} to ${to[1]}` : '';
          
          tolls.push({
            id: crypto.randomUUID(),
            date: formattedDate,
            description: tollRoad + route,
            amount: parseFloat(tollAmountMatch[1]),
            selected: true
          });
          continue;
        }
      }
      
      // Extract gallons - various labels
      const gallonsMatch = section.match(/(?:Gallons?|GAL|Qty\s*\(GAL\)|QTY)[:\s]*(\d+\.?\d*)/i);
      if (!gallonsMatch) continue;
      const gallons = parseFloat(gallonsMatch[1]);
      if (gallons < 1 || gallons > 200) continue; // Sanity check
      
      // Extract total - various labels
      const totalMatch = section.match(/(?:Total Sale|FUEL TOTAL|Total|TOTAL SALE|Net Total)[:\s]*\$?\s*([\d,]+\.?\d{2})/i);
      if (!totalMatch) continue;
      const total = parseFloat(totalMatch[1].replace(/,/g, ''));
      if (total < 5 || total > 500) continue; // Sanity check
      
      // Extract discount/savings if present
      const savingsMatch = section.match(/(?:You saved|Saving)[:\s]*\$?\s*([\d.]+)/i);
      const savings = savingsMatch ? parseFloat(savingsMatch[1]) : 0;
      
      // Check for duplicates
      const exists = fuelExpenses.some(f => 
        f.date === formattedDate && 
        Math.abs(f.totalAmount - total) < 0.01
      );
      
      if (!exists) {
        fuelExpenses.push({
          id: crypto.randomUUID(),
          date: formattedDate,
          location,
          fuelAmount: total,
          fuelDiscount: savings,
          totalAmount: total,
          selected: true
        });
      }
    }
    
    return { fuelExpenses, tolls };
  };

  // Parse Comdata statement text (centralized format)
  const parseComdataStatement = (text: string) => {
    const fuelExpenses: ParsedFuelExpense[] = [];
    const tolls: ParsedToll[] = [];
    
    // Split by lines and process
    const lines = text.split('\n');
    
    for (const line of lines) {
      // Look for date pattern at start: MM/DD/YY
      const dateMatch = line.match(/(\d{1,2}\/\d{1,2}\/\d{2})/);
      if (!dateMatch) continue;
      
      // Check if it's a fuel purchase (has fuel amount > 0)
      const fuelMatch = line.match(/(\d{1,2}\/\d{1,2}\/\d{2})\s+[\d:]+\s+(PILOT|LOVES|TA\d*|FLYING J|SAPP|PETRO)[^\d]*(\d+\.\d{2})\s+(\d+\.\d{2})\s+[\d.]+\s+[\d.]+\s+[\d.]+\s+(\d+\.\d{2})/i);
      
      if (fuelMatch) {
        const [, date, location, fuelAmt, discount, total] = fuelMatch;
        const fuelAmount = parseFloat(fuelAmt);
        
        if (fuelAmount > 0) {
          // Format date
          const parts = date.split('/');
          const formattedDate = `20${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          
          // Extract full location name
          const locationMatch = line.match(/(PILOT|LOVES|TA\s*\d*|FLYING J|SAPP BROS|PETRO|AMBEST)[^|]*/i);
          const fullLocation = locationMatch ? locationMatch[0].trim() : location;
          
          fuelExpenses.push({
            id: crypto.randomUUID(),
            date: formattedDate,
            location: fullLocation.replace(/\s+/g, ' ').trim(),
            fuelAmount: fuelAmount,
            fuelDiscount: parseFloat(discount) || 0,
            totalAmount: parseFloat(total),
            selected: true
          });
          continue;
        }
      }
      
      // Alternative fuel parsing - simpler pattern
      const simpleFuelMatch = line.match(/(\d{1,2}\/\d{1,2}\/\d{2})[^\d]*(PILOT|LOVES|TA\s*\d+|PETRO)[^$\d]*#?\d*[^$\d]*(\d+\.\d{2})/i);
      if (simpleFuelMatch) {
        const [, date, loc] = simpleFuelMatch;
        // Find all numbers in the line
        const numbers = line.match(/\d+\.\d{2}/g);
        if (numbers && numbers.length >= 3) {
          const fuelAmt = parseFloat(numbers[0]);
          const discount = parseFloat(numbers[1]);
          const total = parseFloat(numbers[numbers.length - 1]);
          
          if (fuelAmt > 10) { // Reasonable fuel amount
            const parts = date.split('/');
            const formattedDate = `20${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
            
            // Get full location from line
            const locMatch = line.match(/(PILOT|LOVES|TA)[^|]+/i);
            const fullLoc = locMatch ? locMatch[0].trim() : loc;
            
            // Check if we already added this (avoid duplicates)
            const exists = fuelExpenses.some(f => f.date === formattedDate && Math.abs(f.totalAmount - total) < 0.01);
            if (!exists) {
              fuelExpenses.push({
                id: crypto.randomUUID(),
                date: formattedDate,
                location: fullLoc.replace(/\s+/g, ' ').trim(),
                fuelAmount: fuelAmt,
                fuelDiscount: discount,
                totalAmount: total,
                selected: true
              });
            }
          }
        }
      }
      
      // Check for tolls (state codes like MC901, PA511, etc.)
      const tollMatch = line.match(/(\d{1,2}\/\d{1,2}\/\d{2})\s+[\d:]+\s+([A-Z]{2}\d{3})\s+[^$\d]*[\d.]+\s+[\d.]+\s+[\d.]+\s+[\d.]+\s+[\d.]+\s+(\d+\.\d{2})/i);
      if (tollMatch) {
        const [, date, code, amount] = tollMatch;
        const amt = parseFloat(amount);
        
        if (amt > 0) {
          const parts = date.split('/');
          const formattedDate = `20${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          
          // Map state codes to names
          const stateMap: Record<string, string> = {
            'MC': 'Ohio Turnpike',
            'PA': 'Pennsylvania Turnpike',
            'IN': 'Indiana Toll Road',
            'OH': 'Ohio Toll',
            'KY': 'Kentucky Toll',
            'VA': 'Virginia Toll',
            'NY': 'New York Toll',
            'NJ': 'New Jersey Turnpike',
            'IL': 'Illinois Tollway',
            'TX': 'Texas Toll',
            'VR': 'Various/Other'
          };
          
          const stateCode = code.substring(0, 2);
          const stateName = stateMap[stateCode] || `${stateCode} Toll`;
          
          tolls.push({
            id: crypto.randomUUID(),
            date: formattedDate,
            description: `${stateName} (${code})`,
            amount: amt,
            selected: true
          });
        }
      }
    }
    
    return { fuelExpenses, tolls };
  };

  // Master parse function - tries both statement and individual receipts
  const parseComdataText = (text: string) => {
    let allFuel: ParsedFuelExpense[] = [];
    let allTolls: ParsedToll[] = [];
    
    // Try statement format first (centralized Comdata report)
    const statementResult = parseComdataStatement(text);
    allFuel = [...statementResult.fuelExpenses];
    allTolls = [...statementResult.tolls];
    
    // If no results from statement, try individual receipts format
    if (allFuel.length === 0 && allTolls.length === 0) {
      const receiptResult = parseIndividualReceipts(text);
      allFuel = receiptResult.fuelExpenses;
      allTolls = receiptResult.tolls;
    }
    
    // Combine results if both found (shouldn't happen but be safe)
    // Remove duplicates based on date + amount
    const uniqueFuel = allFuel.filter((fuel, index, self) =>
      index === self.findIndex(f => 
        f.date === fuel.date && Math.abs(f.totalAmount - fuel.totalAmount) < 0.01
      )
    );
    
    const uniqueTolls = allTolls.filter((toll, index, self) =>
      index === self.findIndex(t => 
        t.date === toll.date && Math.abs(t.amount - toll.amount) < 0.01
      )
    );
    
    return { fuelExpenses: uniqueFuel, tolls: uniqueTolls };
  };

  // Extract text from PDF
  const extractTextFromPDF = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      fullText += pageText + '\n\n';
    }
    
    return fullText;
  };

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    setIsLoading(true);
    setError(null);
    setImportStatus(null);
    
    try {
      const text = await extractTextFromPDF(file);
      setPdfText(text);
      
      const { fuelExpenses, tolls } = parseComdataText(text);
      
      if (fuelExpenses.length === 0 && tolls.length === 0) {
        setError('No fuel or toll expenses found. Try pasting the text manually below.');
      } else {
        setParsedFuel(fuelExpenses);
        setParsedTolls(tolls);
      }
    } catch (e: any) {
      console.error('PDF extraction error:', e);
      setError(`Failed to read PDF: ${e.message}. Try pasting the text manually.`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1
  });

  // Parse from pasted text
  const handleParseText = () => {
    if (!pdfText.trim()) return;
    
    setError(null);
    const { fuelExpenses, tolls } = parseComdataText(pdfText);
    
    if (fuelExpenses.length === 0 && tolls.length === 0) {
      setError('No fuel or toll expenses found in the text.');
    } else {
      setParsedFuel(fuelExpenses);
      setParsedTolls(tolls);
    }
  };

  // Toggle selection
  const toggleFuelSelection = (id: string) => {
    setParsedFuel(prev => prev.map(f => f.id === id ? { ...f, selected: !f.selected } : f));
  };

  const toggleTollSelection = (id: string) => {
    setParsedTolls(prev => prev.map(t => t.id === id ? { ...t, selected: !t.selected } : t));
  };

  // Remove items
  const removeFuel = (id: string) => {
    setParsedFuel(prev => prev.filter(f => f.id !== id));
  };

  const removeToll = (id: string) => {
    setParsedTolls(prev => prev.filter(t => t.id !== id));
  };

  // Import selected items
  const handleImport = async () => {
    const selectedFuel = parsedFuel.filter(f => f.selected);
    const selectedTolls = parsedTolls.filter(t => t.selected);
    
    if (selectedFuel.length === 0 && selectedTolls.length === 0) return;
    
    setIsLoading(true);
    let fuelCount = 0;
    let tollCount = 0;
    let errors = 0;
    
    // Import fuel expenses
    for (const fuel of selectedFuel) {
      try {
        await addExpense({
          category: 'fuel',
          description: `${fuel.location} - Comdata`,
          amount: fuel.totalAmount,
          date: fuel.date,
          file: new Blob([`Fuel purchase at ${fuel.location}`], { type: 'text/plain' }),
          filename: `fuel-${fuel.location.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${fuel.date}.pdf`,
        });
        fuelCount++;
      } catch (e) {
        console.error('Failed to import fuel:', e);
        errors++;
      }
    }
    
    // Import tolls as miscellaneous or travel
    for (const toll of selectedTolls) {
      try {
        await addExpense({
          category: 'travel',
          subcategory: 'other',
          description: `Toll - ${toll.description}`,
          amount: toll.amount,
          date: toll.date,
          file: new Blob([`Toll charge: ${toll.description}`], { type: 'text/plain' }),
          filename: `toll-${toll.date}-${toll.description.replace(/[^a-z0-9]/gi, '-')}.pdf`,
        });
        tollCount++;
      } catch (e) {
        console.error('Failed to import toll:', e);
        errors++;
      }
    }
    
    setImportStatus({ fuel: fuelCount, tolls: tollCount, errors });
    
    // Remove imported items from list
    if (fuelCount > 0) {
      setParsedFuel(prev => prev.filter(f => !f.selected));
    }
    if (tollCount > 0) {
      setParsedTolls(prev => prev.filter(t => !t.selected));
    }
    
    setIsLoading(false);
  };

  const selectedFuelCount = parsedFuel.filter(f => f.selected).length;
  const selectedTollCount = parsedTolls.filter(t => t.selected).length;
  const totalFuelAmount = parsedFuel.filter(f => f.selected).reduce((sum, f) => sum + f.totalAmount, 0);
  const totalTollAmount = parsedTolls.filter(t => t.selected).reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Fuel className="w-8 h-8 text-orange-400" />
          Import Comdata Statement
        </h1>
        <p className="text-shuttle-sky mt-1">
          Upload your Bennett Comdata card statement to import fuel & toll expenses
        </p>
      </div>

      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
          isDragActive 
            ? 'border-orange-400 bg-orange-400/10' 
            : 'border-white/30 hover:border-white/50 bg-white/5'
        }`}
      >
        <input {...getInputProps()} />
        <FileUp className="w-12 h-12 mx-auto mb-4 text-orange-400" />
        {isDragActive ? (
          <p className="text-white text-lg">Drop the Comdata PDF here...</p>
        ) : (
          <>
            <p className="text-white text-lg mb-2">
              Drag & drop your Comdata statement PDF here
            </p>
            <p className="text-white/60 text-sm">or click to browse</p>
          </>
        )}
      </div>

      {/* Manual Text Input */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Or Paste Statement Text
        </h3>
        <textarea
          value={pdfText}
          onChange={(e) => setPdfText(e.target.value)}
          placeholder="Paste the text content from your Comdata statement here..."
          className="w-full h-32 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none font-mono text-sm"
        />
        <button
          onClick={handleParseText}
          disabled={!pdfText.trim()}
          className="mt-3 px-6 py-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Parse Statement
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
          <span className="ml-3 text-white">Processing...</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {/* Import Status */}
      {importStatus && (
        <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-green-200">
            Imported {importStatus.fuel} fuel expense(s) and {importStatus.tolls} toll(s)!
            {importStatus.errors > 0 && ` (${importStatus.errors} failed)`}
          </p>
        </div>
      )}

      {/* Parsed Fuel Expenses */}
      {parsedFuel.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between bg-orange-500/10">
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              ⛽ Fuel Expenses ({parsedFuel.length})
            </h3>
            <span className="text-orange-400 font-medium">
              {selectedFuelCount} selected • ${totalFuelAmount.toFixed(2)}
            </span>
          </div>

          <div className="divide-y divide-white/10 max-h-80 overflow-y-auto">
            {parsedFuel.map((fuel) => (
              <div
                key={fuel.id}
                className={`p-4 flex items-center gap-4 transition-colors ${
                  fuel.selected ? 'bg-white/5' : 'bg-transparent opacity-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={fuel.selected}
                  onChange={() => toggleFuelSelection(fuel.id)}
                  className="w-5 h-5 rounded border-white/30 bg-white/10 text-orange-400 focus:ring-orange-400"
                />
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{fuel.location}</p>
                  <p className="text-sm text-white/60">{fuel.date}</p>
                </div>

                <div className="text-right">
                  <p className="text-lg font-bold text-orange-400">${fuel.totalAmount.toFixed(2)}</p>
                  <p className="text-xs text-white/50">Fuel: ${fuel.fuelAmount.toFixed(2)}</p>
                </div>

                <button
                  onClick={() => removeFuel(fuel.id)}
                  className="p-2 text-white/40 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Parsed Tolls */}
      {parsedTolls.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between bg-blue-500/10">
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              🛣️ Toll Expenses ({parsedTolls.length})
            </h3>
            <span className="text-blue-400 font-medium">
              {selectedTollCount} selected • ${totalTollAmount.toFixed(2)}
            </span>
          </div>

          <div className="divide-y divide-white/10 max-h-60 overflow-y-auto">
            {parsedTolls.map((toll) => (
              <div
                key={toll.id}
                className={`p-4 flex items-center gap-4 transition-colors ${
                  toll.selected ? 'bg-white/5' : 'bg-transparent opacity-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={toll.selected}
                  onChange={() => toggleTollSelection(toll.id)}
                  className="w-5 h-5 rounded border-white/30 bg-white/10 text-blue-400 focus:ring-blue-400"
                />
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{toll.description}</p>
                  <p className="text-sm text-white/60">{toll.date}</p>
                </div>

                <div className="text-right">
                  <p className="text-lg font-bold text-blue-400">${toll.amount.toFixed(2)}</p>
                </div>

                <button
                  onClick={() => removeToll(toll.id)}
                  className="p-2 text-white/40 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Import Button */}
      {(parsedFuel.length > 0 || parsedTolls.length > 0) && (
        <div className="flex justify-end">
          <button
            onClick={handleImport}
            disabled={(selectedFuelCount === 0 && selectedTollCount === 0) || isLoading}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-medium hover:from-orange-400 hover:to-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
          >
            <Save className="w-5 h-5" />
            Import {selectedFuelCount + selectedTollCount} Expense(s)
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h3 className="text-lg font-medium text-white mb-3">📋 How to Import Comdata Statement</h3>
        <ol className="space-y-2 text-white/70 text-sm">
          <li>1. Download your monthly Comdata statement from Bennett</li>
          <li>2. Drag & drop the PDF here (or paste the text)</li>
          <li>3. Review the parsed fuel purchases and tolls</li>
          <li>4. Click "Import" to save to cloud</li>
        </ol>
        <div className="mt-4 p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
          <p className="text-orange-300 text-sm">
            💡 <strong>Pro tip:</strong> Fuel expenses are 100% deductible for business use!
          </p>
        </div>
      </div>
    </div>
  );
}

